import { Label } from "@/components/ui/label";

/**
 * ChildAssignPicker
 *
 * Lets the user attribute an expense to one or multiple children.
 * Phase 3 Delivery B removed the legacy "Applies to all children"
 * toggle — pick children explicitly via the chip-row instead.
 * Multi-select is allowed.
 *
 * Extracted from pages/Expenses.jsx in the v77.99.19 Phase 3 frontend
 * decomposition pass. Behaviour and prop shape preserved verbatim.
 *
 * Props:
 *  - childList:   [{ id, name, color }]
 *  - allChildren: boolean   (deprecated, ignored — kept for prop-shape compat)
 *  - childIds:    string[]
 *  - onChange:    (patch) => void   // called with { child_ids?, all_children? }
 *  - testPrefix:  string            // e.g. "expense" or "edit"
 */
export function ChildAssignPicker({
  childList,
  allChildren: _allChildren,
  childIds,
  onChange,
  testPrefix = "expense",
}) {
  const selected = Array.isArray(childIds) ? childIds : [];
  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    onChange({ child_ids: next, all_children: false });
  };

  if (!childList || childList.length === 0) {
    return (
      <div
        className="rounded-md border border-dashed border-[#D9E1E3] p-3 text-xs text-[#6F828A]"
        data-testid={`${testPrefix}-child-picker-empty`}
      >
        No children added yet. This expense will be recorded without child
        attribution. Add children from the Children page to attribute expenses.
      </div>
    );
  }

  return (
    <div data-testid={`${testPrefix}-child-picker`}>
      <Label className="flex items-center justify-between">
        <span>Child(ren) this expense is for</span>
        <span className="text-[10px] uppercase tracking-[0.1em] text-[#825A41] font-normal">
          Required
        </span>
      </Label>
      <div className="mt-1 rounded-md border border-[#D9E1E3] bg-white">
        <div className="p-2 flex flex-wrap gap-2">
          {childList.map((c) => {
            const active = selected.includes(c.id);
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => toggle(c.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs transition-colors ${
                  active
                    ? "bg-[#2E3A3F] border-[#2E3A3F] text-white"
                    : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4]"
                }`}
                data-testid={`${testPrefix}-child-picker-${c.id}`}
                aria-pressed={active}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full border border-black/10"
                  style={{ backgroundColor: c.color || "#D9E1E3" }}
                />
                {c.name}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-1 text-[11px] text-[#6F828A]">
        {selected.length === 0
          ? "Pick one or more children. Multi-select keeps it as a single shared expense."
          : `${selected.length} child${selected.length === 1 ? "" : "ren"} selected.`}
      </div>
    </div>
  );
}

export default ChildAssignPicker;
